// Per (customer, hub) lane settings: load type / capacity / package mix.
// The ONLY place that talks to the `customer_lanes` table.

import { supabase } from "../../lib/supabase";
import type { CustomerLane, LoadType } from "../../lib/types";

interface LaneRow {
  id: string;
  account_id: string;
  customer_id: string;
  hub_id: string;
  load_type: string;
  load_capacity: number | null;
  mix_cartons: number;
  mix_polybags: number;
  mix_irregular: number;
  dow_factors: number[] | null;
}

/** DB row -> app CustomerLane. */
export function rowToLane(row: LaneRow): CustomerLane {
  return {
    hubId: row.hub_id,
    loadType: (row.load_type as LoadType) ?? "mix",
    loadCapacity: row.load_capacity,
    packageMix: {
      cartons: Number(row.mix_cartons),
      polybags: Number(row.mix_polybags),
      irregular: Number(row.mix_irregular),
    },
    dowFactors: Array.isArray(row.dow_factors) ? row.dow_factors : undefined,
  };
}

/** App CustomerLane -> DB row fields. */
export function laneInputToRow(accountId: string, customerId: string, lane: CustomerLane) {
  return {
    account_id: accountId,
    customer_id: customerId,
    hub_id: lane.hubId,
    load_type: lane.loadType,
    load_capacity: lane.loadCapacity ?? null,
    mix_cartons: lane.packageMix.cartons,
    mix_polybags: lane.packageMix.polybags,
    mix_irregular: lane.packageMix.irregular,
    dow_factors: lane.dowFactors ?? null,
  };
}

/**
 * Upsert just the per-hub DOW factors for one lane (used by calibration). On
 * conflict only `dow_factors` is updated — packaging/load columns are preserved.
 * If no lane row exists yet, one is created with column defaults.
 */
export async function saveLaneDowFactors(
  accountId: string,
  customerId: string,
  hubId: string,
  dowFactors: number[]
): Promise<void> {
  const { error } = await supabase
    .from("customer_lanes")
    .upsert(
      { account_id: accountId, customer_id: customerId, hub_id: hubId, dow_factors: dowFactors },
      { onConflict: "customer_id,hub_id" }
    );
  if (error) throw error;
}

/**
 * Clear a lane's calibrated DOW factors (revert that hub to the customer-level
 * profile). Scoped by account + customer + hub; no-op if the lane doesn't exist.
 */
export async function clearLaneDowFactors(
  accountId: string,
  customerId: string,
  hubId: string
): Promise<void> {
  const { error } = await supabase
    .from("customer_lanes")
    .update({ dow_factors: null })
    .eq("account_id", accountId)
    .eq("customer_id", customerId)
    .eq("hub_id", hubId);
  if (error) throw error;
}

/** Load every lane in the account, tagged with its customer id. */
export async function listAccountLanes(
  accountId: string
): Promise<Array<{ customerId: string; lane: CustomerLane }>> {
  const { data, error } = await supabase
    .from("customer_lanes")
    .select("*")
    .eq("account_id", accountId);
  if (error) throw error;
  return (data as LaneRow[]).map((r) => ({ customerId: r.customer_id, lane: rowToLane(r) }));
}

/** Load all lanes for a customer. */
export async function listCustomerLanes(
  accountId: string,
  customerId: string
): Promise<CustomerLane[]> {
  const { data, error } = await supabase
    .from("customer_lanes")
    .select("*")
    .eq("account_id", accountId)
    .eq("customer_id", customerId);
  if (error) throw error;
  return (data as LaneRow[]).map(rowToLane);
}

/** Replace a customer's lanes wholesale (delete all, insert the provided set). */
export async function saveCustomerLanes(
  accountId: string,
  customerId: string,
  lanes: CustomerLane[]
): Promise<void> {
  const { error: delErr } = await supabase
    .from("customer_lanes")
    .delete()
    .eq("account_id", accountId)
    .eq("customer_id", customerId);
  if (delErr) throw delErr;
  if (lanes.length === 0) return;
  const rows = lanes.map((l) => laneInputToRow(accountId, customerId, l));
  const { error } = await supabase.from("customer_lanes").insert(rows);
  if (error) throw error;
}
